// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Bootstrapping and DOM wiring. No engine logic lives here.
 *
 * Everything rendered from a reply or from a folder goes in through
 * `textContent` and `createElement`. `innerHTML` appears nowhere: a reply is
 * untrusted text that this tool has to parse in order to work, and rendering it
 * as markup would turn a `<img src=…>` in a prose paragraph into an
 * exfiltration beacon that fires on paste. The CSP is the second line of that
 * defence; not building HTML from strings is the first.
 */

import './styles/main.css';
import {
  backupEntries,
  buildArtefacts,
  buildProject,
  bundleParts,
  bundleSize,
  includedFiles,
  planChanges,
  planHashFor,
  resultText,
  setAllHunks,
  writeItems,
  type Assignment,
  type Project,
} from './app';
import { copyParts, copyToClipboard, download, downloadBundleZip } from './deliver';
import { EventLog } from './eventlog';
import { initGlossary } from './glossary';
import { makeOverlay } from './overlays';
import { checkPath } from './engine/pathsafe';
import type { SkippedEntry } from './engine/bundle';
import {
  ANDROID_NOTE,
  detect,
  IOS_NOTE,
  IOS_TOO_OLD,
  NO_HANDLES_NOTE,
  WRITE_BLOCKED_NOTE,
} from './fs/capabilities';
import {
  handleFromDrop,
  pickDirectory,
  readDroppedEntries,
  readFileList,
  readHandle,
  readZip,
  shapeOf,
} from './fs/read';
import {
  backupName,
  missingDirectories,
  queryWritePermission,
  requestWritePermission,
  writeFiles,
  writeVerifiedBackup,
} from './fs/write';
import type { FileChange, ParsedBlock } from './types';
import type { DiffResponse, ParseResponse, WorkerRequest, WorkerResponse } from './worker';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const caps = detect();
const log = new EventLog($('drawer'), $('log-count'));

/* ── state ────────────────────────────────────────────────────────────── */

let project: Project | null = null;
let blocks: ParsedBlock[] = [];
let changes: FileChange[] = [];
let skipped: SkippedEntry[] = [];
let unassigned: ParsedBlock[] = [];
const assignments = new Map<number, Assignment>();
const excluded = new Set<string>();
let written = false;
let requestId = 0;

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
const pending = new Map<number, (response: WorkerResponse) => void>();
worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  pending.get(event.data.id)?.(event.data);
  pending.delete(event.data.id);
};

function ask<T extends WorkerResponse>(request: WorkerRequest): Promise<T> {
  return new Promise((resolve) => {
    pending.set(request.id, resolve as (r: WorkerResponse) => void);
    worker.postMessage(request);
  });
}

/* ── overlays ─────────────────────────────────────────────────────────── */

const modalHost = $('modal');
const modalBody = modalHost.querySelector('[data-overlay-body]') as HTMLElement;
const modalTitle = modalHost.querySelector('[data-overlay-title]') as HTMLElement;

/*
 * ONE overlay for the shared modal host, and every open goes through it.
 *
 * An earlier version filled the body and then called
 * `modalHost.removeAttribute('hidden')` directly. The × control and the backdrop
 * both still worked, because they are listeners on the element — but ESCAPE DID
 * NOT, because Escape is served by a document-level handler that closes the
 * TOP OF THE OPEN-OVERLAY STACK, and an overlay opened behind the API's back is
 * never on that stack. Three of sixteen exits failed and nothing else would have
 * noticed. Open only through `overlay.open()`.
 */
const modalOverlay = makeOverlay(modalHost, { onClose: () => modalBody.replaceChildren() });

const MODAL_TITLES: Record<string, string> = {
  how: 'How unfence works',
  privacy: 'Privacy',
  about: 'About unfence',
};

for (const button of document.querySelectorAll<HTMLElement>('[data-modal]')) {
  button.addEventListener('click', () => {
    const which = button.dataset.modal ?? 'about';
    // The host is shared, so the body is refilled on every open — otherwise the
    // second modal shows the first one's prose beneath it.
    modalBody.replaceChildren();
    const template = document.getElementById(`tpl-${which}`) as HTMLTemplateElement | null;
    if (template) modalBody.appendChild(template.content.cloneNode(true));
    modalTitle.textContent = MODAL_TITLES[which] ?? 'unfence';
    modalOverlay.open();
  });
}

$('log-toggle').addEventListener('click', () => log.toggle());
$('copy-log').addEventListener('click', async () => {
  await copyToClipboard(log.text);
});

initGlossary();

/* ── capability notice ────────────────────────────────────────────────── */

function showNotice(text: string): void {
  const note = $('capability-note');
  note.textContent = text;
  note.removeAttribute('hidden');
}

// The iOS note is about not being able to WRITE, so it belongs behind the same
// check as everything else that depends on that. Leading with `isIos` showed it
// to a desktop Mac Chromium during the dry-run, on a machine where writing works.
if (!caps.directoryHandles) showNotice(caps.isIos ? IOS_NOTE : NO_HANDLES_NOTE);
else if (caps.isAndroidChrome) showNotice(ANDROID_NOTE);

/* ── step 1: read a folder ────────────────────────────────────────────── */

const dropzone = $('dropzone');
const readProgress = $('read-progress');
const readBar = $('read-bar');
const readText = $('read-text');

function setProgress(done: number, total: number, label: string): void {
  readProgress.removeAttribute('hidden');
  readBar.style.width = `${total ? Math.round((done / total) * 100) : 0}%`;
  readText.textContent = label;
}

async function ingest(load: () => Promise<Awaited<ReturnType<typeof readHandle>>>, source: string): Promise<void> {
  try {
    setProgress(0, 1, 'reading…');
    const result = await load();
    if (result.files.length === 0) {
      log.warn(`${source}: no files found`);
      readProgress.setAttribute('hidden', '');
      showNotice('No files were found there. If you picked a folder on an iPhone, drop a .zip of the project instead.');
      return;
    }
    log.info(`${source}: ${result.files.length} files`);
    project = await buildProject(
      result,
      (done, total) => setProgress(done, total, `reading ${done} of ${total} files`),
      caps.hidesDotfiles,
    );
    readProgress.setAttribute('hidden', '');
    renderFolder();
    if (blocks.length) void reparse();
  } catch (error) {
    readProgress.setAttribute('hidden', '');
    const message = error instanceof Error ? error.message : String(error);
    if (/abort/i.test(message)) return; // the user closed the picker
    log.bad(`could not read the folder: ${message}`);
    showNotice(
      `unfence could not read that folder: ${message}. Chrome refuses your home folder, Desktop, ` +
        'Documents and Downloads themselves — a project inside them is fine.',
    );
  }
}

$('pick-folder').addEventListener('click', () => {
  if (caps.directoryHandles) {
    void ingest(async () => readHandle(await pickDirectory(), (n, p) => setProgress(n, n + 1, `${n} files · ${p}`)), 'folder');
    return;
  }
  const input = document.createElement('input');
  input.type = 'file';
  (input as unknown as { webkitdirectory: boolean }).webkitdirectory = true;
  input.multiple = true;
  input.addEventListener('change', () => {
    const shape = shapeOf(input.files ?? []);
    // Three outcomes, not two. An EMPTY FileList is iOS below 18.4, where the
    // property exists, can be set, and does nothing — with no error at all.
    // Empty webkitRelativePath is Firefox Android, where folder selection works
    // but the paths do not; that is a route to drag-and-drop, not a failure.
    if (shape === 'empty') {
      showNotice(caps.isIos ? IOS_TOO_OLD : 'That folder came back empty. Try dropping a .zip instead.');
      return;
    }
    if (shape === 'flat-no-paths') {
      showNotice('This browser returned the files without their folder structure. Drag the folder onto the page instead, or drop a .zip.');
      return;
    }
    void ingest(async () => readFileList(input.files ?? []), 'folder');
  });
  input.click();
});

$('pick-zip').addEventListener('click', () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.zip,application/zip';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void ingest(() => readZip(file, file.name), file.name);
  });
  input.click();
});

dropzone.addEventListener('click', (event) => {
  // The sample pill lives inside the dropzone, so its click must not also open
  // the file picker.
  if ((event.target as HTMLElement).closest('#try-sample, button')) return;
  $('pick-folder').click();
});
dropzone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    $('pick-folder').click();
  }
});

for (const type of ['dragenter', 'dragover'] as const) {
  dropzone.addEventListener(type, (event) => {
    event.preventDefault();
    dropzone.classList.add('dragging');
  });
}
for (const type of ['dragleave', 'drop'] as const) {
  dropzone.addEventListener(type, () => dropzone.classList.remove('dragging'));
}
dropzone.addEventListener('drop', (event) => {
  event.preventDefault();
  const transfer = event.dataTransfer;
  if (!transfer) return;

  const zip = Array.from(transfer.files).find((f) => /\.zip$/i.test(f.name));
  if (zip) {
    void ingest(() => readZip(zip, zip.name), zip.name);
    return;
  }
  if (caps.dropHandles) {
    void (async () => {
      const handle = await handleFromDrop(transfer.items);
      if (handle) {
        await ingest(() => readHandle(handle, (n, p) => setProgress(n, n + 1, `${n} files · ${p}`)), 'dropped folder');
        return;
      }
      await ingest(() => readDroppedEntries(transfer.items), 'dropped folder');
    })();
    return;
  }
  void ingest(() => readDroppedEntries(transfer.items), 'dropped folder');
});

$('try-sample').addEventListener('click', async (event) => {
  event.stopPropagation();
  try {
    log.info('loading the sample project');
    const [zip, reply] = await Promise.all([
      fetch('samples/small-app.zip').then((r) => r.blob()),
      fetch('samples/model-reply.md').then((r) => r.text()),
    ]);
    // Straight through the real ingestion path — the same call a dropped zip
    // makes — so the demo exercises the product rather than a shortcut.
    await ingest(() => readZip(zip, 'small-app.zip'), 'sample');
    const box = $<HTMLTextAreaElement>('reply');
    box.value = reply;
    await reparse();
  } catch (error) {
    log.bad(`sample failed to load: ${error instanceof Error ? error.message : String(error)}`);
  }
});

/* ── step 2: the bundle ───────────────────────────────────────────────── */

function renderFolder(): void {
  if (!project) return;
  const summary = $('folder-summary');
  summary.replaceChildren();
  summary.removeAttribute('hidden');

  const included = includedFiles(project, { excluded });
  const size = bundleSize(project, { excluded });

  const stats = document.createElement('div');
  stats.className = 'summary';
  for (const [value, label] of [
    [String(included.length), 'files in the bundle'],
    [String(project.omitted.length), 'left out'],
    [size.label.replace('≈', ''), 'estimated size'],
  ] as [string, string][]) {
    const stat = document.createElement('div');
    stat.className = 'stat';
    const v = document.createElement('span');
    v.className = 'stat-value';
    v.textContent = value;
    const l = document.createElement('span');
    l.className = 'stat-label';
    l.textContent = label;
    stat.append(v, l);
    stats.appendChild(stat);
  }
  summary.appendChild(stats);

  if (project.truncated) {
    const note = document.createElement('p');
    note.className = 'hint';
    note.textContent = 'That folder is very large, so only the first few thousand files were read.';
    summary.appendChild(note);
  }
  if (project.hidDotfiles) {
    const note = document.createElement('p');
    note.className = 'hint';
    note.textContent =
      'This browser hides dot-files from web pages, so your .gitignore could not be read here and ignore rules were not applied. Check the file list before copying.';
    summary.appendChild(note);
  }
  if (project.deadRules.length) {
    const note = document.createElement('p');
    note.className = 'hint';
    note.textContent = `${project.deadRules.length} line(s) in your .gitignore can never match, because a parent directory is already excluded: ${project.deadRules
      .map((r) => r.source)
      .slice(0, 3)
      .join(', ')}`;
    summary.appendChild(note);
  }
  if (project.leaks.length) {
    const note = document.createElement('p');
    note.className = 'hint';
    note.textContent = `A value from a refused .env file also appears in ${project.leaks.length} included file(s) — ${project.leaks
      .map((l) => l.path)
      .slice(0, 3)
      .join(', ')}. This check is best effort and cannot be a guarantee.`;
    summary.appendChild(note);
  }

  $('step-bundle').removeAttribute('hidden');
  $('step-reply').removeAttribute('hidden');
  $('bundle-lede').textContent =
    `${project.folderName} — ${included.length} files, ${size.label}. Paste this into your assistant, ask for what you want, then bring the answer back below.`;
  $('bundle-hint').textContent =
    'The size is an estimate from measured bytes-per-token ratios, accurate to about ±9%. It is not an exact token count — no tokenizer is downloaded.';
  renderFileList();
  log.good(`${project.folderName}: ${included.length} files in, ${project.omitted.length} left out`);
}

function renderFileList(): void {
  if (!project) return;
  const list = $('filelist');
  list.replaceChildren();
  const rows = [...project.files].sort((a, b) => a.path.localeCompare(b.path));
  for (const file of rows) {
    const row = document.createElement('div');
    row.className = file.ignored || excluded.has(file.path) ? 'file-row is-out' : 'file-row';
    const path = document.createElement('span');
    path.className = 'file-path';
    path.textContent = file.path;
    const why = document.createElement('span');
    why.className = 'file-why';
    const omission = project.omitted.find((o) => o.path === file.path);
    why.textContent = excluded.has(file.path) ? 'you excluded it' : (omission?.reason ?? '');
    const size = document.createElement('span');
    size.className = 'file-size';
    size.textContent = `${Math.max(1, Math.round(file.size / 1024))} kB`;
    row.append(path, why, size);
    list.appendChild(row);
  }
}

$('toggle-files').addEventListener('click', () => {
  const list = $('filelist');
  const showing = !list.hasAttribute('hidden');
  if (showing) list.setAttribute('hidden', '');
  else list.removeAttribute('hidden');
  $('toggle-files').textContent = showing ? 'Show files' : 'Hide files';
});

$('copy-bundle').addEventListener('click', async () => {
  if (!project) return;
  const parts = bundleParts(project, { excluded });
  const ok = await copyParts(parts);
  $('bundle-hint').textContent = ok
    ? 'Copied. Paste it into your assistant, then bring the whole answer back to step 3.'
    : 'Your browser would not let the page write to the clipboard — use “Download bundle.md” instead.';
  log[ok ? 'good' : 'warn'](ok ? 'bundle copied to the clipboard' : 'clipboard write refused');
});

$('download-bundle').addEventListener('click', () => {
  if (!project) return;
  // Synchronously inside the handler — after an await, Chromium's download
  // limiter has already flipped and the file can silently never arrive.
  download('bundle.md', bundleParts(project, { excluded }), 'text/markdown;charset=utf-8');
  log.good('bundle.md downloaded');
});

/* ── step 3: parse the reply ──────────────────────────────────────────── */

const replyBox = $<HTMLTextAreaElement>('reply');
let parseTimer: number | undefined;

replyBox.addEventListener('input', () => {
  window.clearTimeout(parseTimer);
  parseTimer = window.setTimeout(() => void reparse(), 300);
});

$('paste-reply').addEventListener('click', async () => {
  try {
    replyBox.value = await navigator.clipboard.readText();
    await reparse();
  } catch {
    $('parse-status').textContent = 'Your browser would not let the page read the clipboard — paste with Ctrl-V.';
    replyBox.focus();
  }
});

$('clear-reply').addEventListener('click', () => {
  replyBox.value = '';
  blocks = [];
  changes = [];
  assignments.clear();
  written = false;
  $('step-review').setAttribute('hidden', '');
  $('parse-status').textContent = '';
});

async function reparse(): Promise<void> {
  const reply = replyBox.value;
  if (!reply.trim()) {
    $('parse-status').textContent = '';
    $('step-review').setAttribute('hidden', '');
    return;
  }
  const paths = project ? project.files.map((f) => f.path) : [];
  const basenames = paths.map((p) => p.slice(p.lastIndexOf('/') + 1).toLowerCase());

  const response = await ask<ParseResponse>({ type: 'parse', id: ++requestId, reply, basenames, paths });
  blocks = response.blocks;
  written = false;

  const recovered = blocks.filter((b) => b.path).length;
  $('parse-status').textContent =
    `${blocks.length} code block${blocks.length === 1 ? '' : 's'} · ${recovered} placed automatically`;
  log.info(`parsed ${blocks.length} blocks, ${recovered} with a recoverable path`);

  if (!project) {
    $('parse-status').textContent += ' — choose your folder above to see the diff';
    return;
  }
  await replan();
}

/* ── step 4: plan, review, deliver ────────────────────────────────────── */

async function replan(): Promise<void> {
  if (!project) return;
  const planned = planChanges(project, blocks, assignments);
  skipped = planned.skipped;
  unassigned = planned.unassigned;

  const response = await ask<DiffResponse>({
    type: 'diff',
    id: ++requestId,
    jobs: planned.jobs.map((j) => ({ ...j })),
  });

  changes = [];
  for (const outcome of response.results) {
    const job = planned.jobs.find((j) => j.blockIndex === outcome.blockIndex && j.path === outcome.path);
    if (!job) continue;
    if (outcome.tooLarge) {
      skipped.push({ label: outcome.path, reason: 'This file is too large to diff safely in a browser tab.' });
      continue;
    }
    if (outcome.identical) {
      skipped.push({ label: outcome.path, reason: 'Identical to what is already on disk — nothing to do.' });
      continue;
    }
    changes.push({
      path: outcome.path,
      kind: outcome.kind,
      blockIndex: outcome.blockIndex,
      oldText: job.oldText,
      newText: job.newText,
      eol: project.files.find((f) => f.path === outcome.path)?.eol ?? '\n',
      hunks: outcome.hunks.map((h) => ({ ...h })),
      elision: outcome.elision,
      verdict: checkPath(outcome.path),
      overridden: assignments.get(outcome.blockIndex)?.overridden ?? false,
      planHash: planHashFor(project, outcome.path),
    });
  }

  $('step-review').removeAttribute('hidden');
  renderQueue();
  renderChanges();
  renderSkipped();
  renderDeliver();
}

function renderQueue(): void {
  const queue = $('queue');
  queue.replaceChildren();
  if (unassigned.length === 0) {
    queue.setAttribute('hidden', '');
    return;
  }
  queue.removeAttribute('hidden');

  const heading = document.createElement('h3');
  heading.textContent = `${unassigned.length} block${unassigned.length === 1 ? '' : 's'} to place`;
  const lede = document.createElement('p');
  lede.className = 'queue-lede';
  lede.textContent =
    'Most code blocks in a real answer carry no filename — the model just writes the code. Type where each one goes, or leave it and it will be listed as skipped.';
  queue.append(heading, lede);

  const paths = project?.files.map((f) => f.path) ?? [];
  const datalistId = 'known-paths';
  let datalist = document.getElementById(datalistId) as HTMLDataListElement | null;
  if (!datalist) {
    datalist = document.createElement('datalist');
    datalist.id = datalistId;
    document.body.appendChild(datalist);
  }
  datalist.replaceChildren();
  for (const path of paths.slice(0, 2000)) {
    const option = document.createElement('option');
    option.value = path;
    datalist.appendChild(option);
  }

  for (const block of unassigned) {
    const item = document.createElement('div');
    item.className = 'queue-item';

    const head = document.createElement('div');
    head.className = 'queue-head';
    const label = document.createElement('span');
    label.className = 'queue-label';
    label.textContent = `Block ${block.index + 1}`;
    const lang = document.createElement('span');
    lang.className = 'queue-lang';
    lang.textContent = block.info || 'no language';
    head.append(label, lang);

    const preview = document.createElement('pre');
    preview.className = 'queue-preview';
    preview.textContent = block.content.split('\n').slice(0, 4).join('\n');

    const row = document.createElement('div');
    row.className = 'assign-row';
    const input = document.createElement('input');
    input.className = 'assign-input';
    input.type = 'text';
    input.placeholder = 'src/where/this/goes.ts';
    input.setAttribute('list', datalistId);
    input.setAttribute('aria-label', `File path for block ${block.index + 1}`);
    const assign = document.createElement('button');
    assign.className = 'btn btn-sm btn-primary';
    assign.textContent = 'Place it';
    const problem = document.createElement('span');
    problem.className = 'hint';

    const commit = () => {
      const value = input.value.trim();
      if (!value) return;
      const verdict = checkPath(value);
      if (!verdict.ok && !verdict.overridable) {
        problem.textContent = verdict.reason ?? 'That path was refused.';
        return;
      }
      assignments.set(block.index, { path: value, overridden: false });
      log.info(`placed block ${block.index + 1} at ${value}`);
      void replan();
    };
    assign.addEventListener('click', commit);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      }
    });

    row.append(input, assign, problem);
    item.append(head, preview, row);
    queue.appendChild(item);
  }
}

function renderChanges(): void {
  const host = $('changes');
  host.replaceChildren();

  for (const change of changes) {
    const card = document.createElement('div');
    card.className = 'change';
    const refused = (!change.verdict.ok || change.elision.elided) && !change.overridden;
    if (refused) card.classList.add('is-refused');

    const head = document.createElement('div');
    head.className = 'change-head';
    const path = document.createElement('span');
    path.className = 'change-path';
    path.textContent = change.path;
    head.appendChild(path);

    const tag = document.createElement('span');
    tag.className = change.kind === 'create' ? 'tag tag-new' : 'tag tag-mod';
    tag.textContent = change.kind === 'create' ? 'new file' : 'modified';
    head.appendChild(tag);

    const block = blocks.find((b) => b.index === change.blockIndex);
    if (block?.evidence) {
      const evidence = document.createElement('span');
      evidence.className = 'evidence';
      evidence.textContent = `from ${block.evidence}`;
      head.appendChild(evidence);
    }
    if (block?.languageMismatch) {
      const warn = document.createElement('span');
      warn.className = 'tag tag-warn';
      warn.textContent = 'check the extension';
      head.appendChild(warn);
    }
    for (const segment of change.verdict.mixedScriptSegments) {
      const warn = document.createElement('span');
      warn.className = 'tag tag-warn';
      warn.textContent = `mixed scripts in “${segment}”`;
      head.appendChild(warn);
    }
    card.appendChild(head);

    const body = document.createElement('div');
    body.className = 'change-body';

    if (!change.verdict.ok) {
      body.appendChild(
        refusalBanner(
          change,
          `Refused: ${change.verdict.reason}`,
          change.verdict.overridable
            ? 'Your browser does not stop this — the refusal is unfence’s. Type OVERWRITE to write it anyway.'
            : 'This one cannot be overridden.',
          change.verdict.overridable,
        ),
      );
    } else if (change.elision.elided) {
      body.appendChild(
        refusalBanner(
          change,
          `Refused: this replaces ${change.elision.deleted} lines with ${change.elision.added || 'nothing'} ` +
            `${change.elision.added === 1 ? 'comment line' : 'comment lines'}` +
            (change.elision.marker ? ` — “${change.elision.marker}”` : '') +
            '. That is a partial file about to be written over a whole one.',
          'If the deletion is genuine, read every hunk below and then type OVERWRITE.',
          true,
        ),
      );
    }

    for (const [index, hunk] of change.hunks.entries()) {
      const section = document.createElement('div');
      section.className = hunk.accepted ? 'hunk' : 'hunk rejected';

      const hunkHead = document.createElement('div');
      hunkHead.className = 'hunk-head';
      const range = document.createElement('span');
      range.textContent = `@@ -${hunk.oldStart},${hunk.oldLines} +${hunk.newStart},${hunk.newLines} @@`;
      const toggle = document.createElement('label');
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = hunk.accepted;
      box.addEventListener('change', () => {
        change.hunks[index] = { ...hunk, accepted: box.checked };
        renderChanges();
        renderDeliver();
      });
      toggle.append(box, document.createTextNode(hunk.accepted ? 'accepted' : 'rejected'));
      hunkHead.append(range, toggle);
      section.appendChild(hunkHead);

      const lines = document.createElement('pre');
      lines.className = 'hunk-lines';
      for (const line of hunk.lines) {
        const row = document.createElement('span');
        const marker = line[0];
        row.className =
          marker === '+' ? 'dline dline-add' : marker === '-' ? 'dline dline-del' : marker === '\\' ? 'dline dline-meta' : 'dline';
        row.textContent = line;
        lines.appendChild(row);
      }
      section.appendChild(lines);
      body.appendChild(section);
    }

    card.appendChild(body);
    host.appendChild(card);
  }
}

function refusalBanner(change: FileChange, what: string, how: string, overridable: boolean): HTMLElement {
  const banner = document.createElement('div');
  banner.className = 'banner banner-bad';
  const first = document.createElement('p');
  first.textContent = what;
  const second = document.createElement('p');
  second.textContent = how;
  banner.append(first, second);

  if (overridable) {
    const row = document.createElement('div');
    row.className = 'override-row';
    const input = document.createElement('input');
    input.className = 'override-input';
    input.placeholder = 'type OVERWRITE';
    input.setAttribute('aria-label', `Type OVERWRITE to allow writing ${change.path}`);
    const confirm = document.createElement('button');
    confirm.className = 'btn btn-sm btn-danger';
    confirm.textContent = 'Write it anyway';
    confirm.addEventListener('click', () => {
      // A typed confirmation, never a plain button. The refusals reachable from
      // here are the ones with real consequences — a file that runs code, or a
      // whole file about to be replaced by a stub — and a single misplaced
      // click should not be able to reach them.
      if (input.value.trim().toUpperCase() !== 'OVERWRITE') {
        input.focus();
        return;
      }
      const existing = assignments.get(change.blockIndex);
      assignments.set(change.blockIndex, { path: existing?.path ?? change.path, overridden: true });
      log.warn(`override accepted for ${change.path}`);
      void replan();
    });
    row.append(input, confirm);
    banner.appendChild(row);
  }
  return banner;
}

function renderSkipped(): void {
  const host = $('skipped');
  host.replaceChildren();
  if (skipped.length === 0) {
    host.setAttribute('hidden', '');
    return;
  }
  host.removeAttribute('hidden');
  const heading = document.createElement('h3');
  heading.textContent = `${skipped.length} not written`;
  host.appendChild(heading);
  for (const entry of skipped) {
    const row = document.createElement('div');
    row.className = 'skip-row';
    const label = document.createElement('span');
    label.className = 'skip-label';
    label.textContent = entry.label;
    const why = document.createElement('span');
    why.className = 'skip-why';
    why.textContent = ` — ${entry.reason}`;
    row.append(label, why);
    host.appendChild(row);
  }
}

function renderDeliver(): void {
  const host = $('deliver');
  host.replaceChildren();
  const items = writeItems(changes);
  if (items.length === 0 && changes.length === 0) {
    host.setAttribute('hidden', '');
    return;
  }
  host.removeAttribute('hidden');

  const artefacts = buildArtefacts(changes, skipped, written);
  const heading = document.createElement('h3');
  heading.textContent = written ? 'Written' : `${items.length} file${items.length === 1 ? '' : 's'} ready`;
  const lede = document.createElement('p');
  lede.textContent = written
    ? 'The files are in your folder. The patch below is a record of what was written — do not run git apply on it.'
    : project?.handle
      ? 'unfence will write a verified backup zip first. If the backup cannot be written, nothing is.'
      : 'This browser cannot write to your folder, so take the patch and the zip. Run git apply from the top level of your repository.';
  host.append(heading, lede);

  const row = document.createElement('div');
  row.className = 'row';

  if (project?.handle && !written && items.length > 0) {
    const write = document.createElement('button');
    write.className = 'btn btn-primary';
    write.textContent = `Back up and write ${items.length} file${items.length === 1 ? '' : 's'}`;
    write.addEventListener('click', () => void runWrite(write));
    row.appendChild(write);
  }

  const patchButton = document.createElement('button');
  patchButton.className = written ? 'btn' : 'btn btn-primary';
  patchButton.textContent = written ? 'Download the record' : 'Download changes.patch';
  patchButton.addEventListener('click', () => {
    download(written ? `applied-${Date.now()}.patch` : 'changes.patch', [artefacts.patch]);
    log.good('patch downloaded');
  });
  row.appendChild(patchButton);

  const zipButton = document.createElement('button');
  zipButton.className = 'btn';
  zipButton.textContent = 'Download everything (.zip)';
  zipButton.addEventListener('click', () => {
    // One download per gesture: everything goes in a single zip rather than
    // four `<a download>` clicks, the second of which Chromium turns into a
    // prompt the user may never see.
    void downloadBundleZip('unfence-changes.zip', [
      { name: written ? 'applied.patch' : 'changes.patch', text: artefacts.patch },
      { name: 'skipped.md', text: artefacts.skippedText },
      ...changes
        .filter((c) => c.hunks.some((h) => h.accepted))
        .map((c) => ({ name: `updated/${c.path}`, text: resultText(c) })),
    ]);
    log.good('artefacts downloaded');
  });
  row.appendChild(zipButton);

  const acceptAll = document.createElement('button');
  acceptAll.className = 'btn btn-sm';
  acceptAll.textContent = 'Accept all hunks';
  acceptAll.addEventListener('click', () => {
    for (const change of changes) setAllHunks(change.hunks, true);
    renderChanges();
    renderDeliver();
  });
  row.appendChild(acceptAll);
  host.appendChild(row);

  const invariants = document.createElement('ul');
  invariants.className = 'invariants';
  for (const invariant of artefacts.invariants) {
    const item = document.createElement('li');
    if (!invariant.ok) item.className = 'failed';
    item.textContent = `changes.patch ${invariant.name}`;
    invariants.appendChild(item);
  }
  host.appendChild(invariants);
}

/* ── writing ──────────────────────────────────────────────────────────── */

async function runWrite(button: HTMLButtonElement): Promise<void> {
  if (!project?.handle) return;
  const handle = project.handle;
  const items = writeItems(changes);
  button.disabled = true;

  try {
    // Permission first. `queryPermission` needs no gesture; `requestPermission`
    // does, and this is still inside the click.
    let permission = await queryWritePermission(handle);
    if (permission !== 'granted') permission = await requestWritePermission(handle);
    if (permission !== 'granted') {
      showNotice(WRITE_BLOCKED_NOTE);
      log.warn('write permission refused — patch and zip only');
      button.disabled = false;
      return;
    }

    // A hallucinated `srcc/app.ts` would otherwise silently grow a new tree that
    // looks, in the review above, exactly like an ordinary new file.
    const missing = await missingDirectories(handle, items.map((i) => i.path));
    let allowNewDirectories = missing.length === 0;
    if (missing.length > 0) {
      allowNewDirectories = window.confirm(
        `The reply names ${missing.length} folder(s) that do not exist yet:\n\n${missing.join('\n')}\n\n` +
          'Create them? If one of these is a misspelling, cancel and fix the path instead.',
      );
      if (!allowNewDirectories) {
        log.warn('new folders declined — nothing written');
        button.disabled = false;
        return;
      }
    }

    const entries = backupEntries(project, items);
    let backupBytes = 0;
    let name: string | null = null;

    if (entries.length > 0) {
      name = backupName();
      const save = (window as unknown as { showSaveFilePicker(o: object): Promise<FileSystemFileHandle> })
        .showSaveFilePicker;
      if (typeof save !== 'function') throw new Error('this browser cannot save a backup, so nothing was written');
      const destination = await save({
        suggestedName: name,
        types: [{ description: 'Zip archive', accept: { 'application/zip': ['.zip'] } }],
      });
      log.info(`writing the backup (${entries.length} files)`);
      // A HARD GATE. Written, closed, reopened, and checked against the exact
      // predicted length and the end-of-archive marker. An unverified net is no
      // net, so a failure here means zero writes.
      backupBytes = await writeVerifiedBackup(destination, entries);
      log.good(`backup verified: ${backupBytes} bytes`);
    } else {
      log.info('every file is new, so there is nothing to back up');
    }

    const outcome = await writeFiles(handle, items, {
      allowNewDirectories,
      onEvent: (level, text) => log.add(level, text),
    });

    written = true;
    log.good(`${outcome.written.length} written, ${outcome.skipped.length} skipped, ${outcome.failed.length} failed`);
    for (const s of outcome.skipped) skipped.push({ label: s.path, reason: s.reason });
    for (const f of outcome.failed) skipped.push({ label: f.path, reason: `write failed: ${f.reason}` });

    if (outcome.failed.length) {
      showNotice(
        `unfence stopped at ${outcome.failed[0].path}: ${outcome.failed[0].reason}. ` +
          `${outcome.written.length} file(s) were written before that${name ? `, and your backup is in ${name}` : ''}.`,
      );
    }
    renderSkipped();
    renderDeliver();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (/abort/i.test(message)) {
      log.info('backup cancelled — nothing was written');
    } else {
      log.bad(message);
      showNotice(`Nothing was written: ${message}`);
    }
    button.disabled = false;
  }
}

/* ── keyboard ─────────────────────────────────────────────────────────── */

document.addEventListener('keydown', (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v' && document.activeElement === document.body) {
    replyBox.focus();
  }
});

log.info('unfence ready — nothing has been sent anywhere');
if (!caps.directoryHandles) log.warn('this browser cannot write to a folder; patch and zip only');
